import { useState } from "react";
import { UserPlus, Trash2, Loader2, Crown, Shield, Wrench, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAdminTeam, type AdminTeamMember } from "@/admin/hooks/useAdminTeam";
import { useAdminAuth } from "@/admin/hooks/useAdminAuth";
import { Navigate } from "react-router-dom";
import type { AdminRole } from "@/admin/types/admin";

// ─── helpers ──────────────────────────────────────────────────────────────────

const ROLE_CONFIG: Record<AdminRole, {
  label: string;
  description: string;
  icon: React.ElementType;
  badgeClass: string;
  cardClass: string;
}> = {
  adminOwner: {
    label: "Owner",
    description: "Accès complet — peut gérer l'équipe admin",
    icon: Crown,
    badgeClass: "bg-amber-50 text-amber-700 ring-amber-200",
    cardClass: "border-amber-100 bg-amber-50/30",
  },
  adminModerator: {
    label: "Modérateur",
    description: "Vérifications, utilisateurs, annonces, signalements",
    icon: Shield,
    badgeClass: "bg-blue-50 text-blue-700 ring-blue-200",
    cardClass: "border-blue-100 bg-white",
  },
  adminTech: {
    label: "Tech",
    description: "Audit log en lecture seule",
    icon: Wrench,
    badgeClass: "bg-gray-100 text-gray-600 ring-gray-200",
    cardClass: "border-gray-100 bg-white",
  },
};

function RoleBadge({ role }: { role: AdminRole }) {
  const { label, icon: Icon, badgeClass } = ROLE_CONFIG[role];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${badgeClass}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

function Avatar({ name, url }: { name: string; url: string | null }) {
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (url) return <img src={url} alt={name} className="h-9 w-9 rounded-full object-cover" />;
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600 flex-shrink-0">
      {initials || "?"}
    </div>
  );
}

// ─── composant modal invitation ───────────────────────────────────────────────

const INVITABLE_ROLES: { value: AdminRole; label: string; description: string; icon: React.ElementType }[] = [
  {
    value: "adminModerator",
    label: "Modérateur",
    description: "Vérifications artisans, gestion utilisateurs, modération annonces et signalements",
    icon: Shield,
  },
  {
    value: "adminTech",
    label: "Tech",
    description: "Accès en lecture seule à l'audit log",
    icon: Wrench,
  },
];

function InviteModal({
  open,
  onClose,
  onConfirm,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (email: string, role: AdminRole) => void;
  loading: boolean;
}) {
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState<AdminRole>("adminModerator");

  const handleSubmit = () => {
    if (!email.trim()) return;
    onConfirm(email.trim(), selectedRole);
  };

  const handleClose = () => {
    setEmail("");
    setSelectedRole("adminModerator");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajouter un administrateur</DialogTitle>
          <DialogDescription>
            L'utilisateur doit déjà avoir un compte Hirafi. Il sera promu au rôle choisi.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Email */}
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email du compte Hirafi *</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="prenom.nom@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>

          {/* Choix du rôle */}
          <div className="space-y-2">
            <Label>Rôle *</Label>
            <div className="space-y-2">
              {INVITABLE_ROLES.map(({ value, label, description, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSelectedRole(value)}
                  className={`w-full rounded-lg border p-3 text-left transition-all ${
                    selectedRole === value
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${
                      selectedRole === value ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-500"
                    }`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{label}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{description}</p>
                    </div>
                    {selectedRole === value && (
                      <div className="ml-auto mt-1 h-4 w-4 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L5 6.586 3.707 5.293z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Note Owner non créable */}
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-xs text-amber-700">
            <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>Le rôle <strong>Owner</strong> ne peut pas être attribué via cette interface.</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!email.trim() || loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── page principale ──────────────────────────────────────────────────────────

export default function AdminTeam() {
  const { can, adminUserId } = useAdminAuth();
  const { query, promoteMutation, removeMutation } = useAdminTeam();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<AdminTeamMember | null>(null);

  // Guard : seul adminOwner peut accéder
  if (!can("manage_admins")) {
    return <Navigate to="/admin" replace />;
  }

  const members = query.data ?? [];

  const handleInvite = (email: string, role: AdminRole) => {
    promoteMutation.mutate(
      { email, adminRole: role },
      { onSuccess: () => setInviteOpen(false) }
    );
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Équipe admin</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Gérez les accès administrateurs — {members.length} membre(s)
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="gap-2 flex-shrink-0">
          <UserPlus className="h-4 w-4" />
          Ajouter
        </Button>
      </div>

      {/* Matrice des permissions */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(Object.entries(ROLE_CONFIG) as [AdminRole, typeof ROLE_CONFIG[AdminRole]][]).map(
          ([role, config]) => {
            const { label, description, icon: Icon, badgeClass, cardClass } = config;
            return (
              <div key={role} className={`rounded-xl border p-4 ${cardClass}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${badgeClass}`}>
                    <Icon className="h-3 w-3" />
                    {label}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{description}</p>
              </div>
            );
          }
        )}
      </div>

      {/* Liste membres */}
      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <p className="text-gray-400">Aucun administrateur trouvé</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((member) => {
            const isSelf = member.adminUserId === adminUserId;
            const isOwner = member.adminRole === "adminOwner";

            return (
              <div
                key={member.adminUserId}
                className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
              >
                <Avatar name={member.displayName} url={member.avatarUrl} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-900 truncate">{member.displayName}</p>
                    {isSelf && (
                      <span className="text-[10px] rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">vous</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate">{member.email}</p>
                  <p className="text-[10px] text-gray-300 mt-0.5">
                    Ajouté par {member.createdByDisplayName} · {new Date(member.createdAt).toLocaleDateString("fr-FR")}
                  </p>
                </div>

                <RoleBadge role={member.adminRole} />

                {/* Action retrait — désactivée pour soi-même et pour les Owners */}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                  disabled={isSelf || isOwner}
                  title={
                    isSelf
                      ? "Impossible de se retirer soi-même"
                      : isOwner
                      ? "Impossible de retirer un Owner"
                      : "Révoquer les droits admin"
                  }
                  onClick={() => setRemoveTarget(member)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal invitation */}
      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onConfirm={handleInvite}
        loading={promoteMutation.isPending}
      />

      {/* Confirmation révocation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Révoquer les droits admin ?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{removeTarget?.displayName}</strong> perdra tout accès à l'espace admin et
              retrouvera un rôle client standard. Cette action est réversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!removeTarget) return;
                removeMutation.mutate(removeTarget.adminUserId, {
                  onSuccess: () => setRemoveTarget(null),
                });
              }}
            >
              {removeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Révoquer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
